/**
 * Device-session refresh for the native bridge (POST /api/auth/device/refresh,
 * W11-BRIDGEKEY). Mirrors the MAUI AuthService refresh:
 *
 *   - Registration returns a short-lived (1 h) bound token plus an opaque,
 *     rotating refresh credential. Both live in the Keystore vault
 *     (native-token-vault.ts), never in localStorage.
 *   - Each rotation uses a random nonce that is STORED before it is sent and
 *     kept until the answer is stored, so a retry after a lost answer (or an
 *     app restart) presents the same credential and nonce and gets the same
 *     successor instead of a reuse revoke. A nonce that cannot be stored is
 *     never sent.
 *   - A key-bound session (registered with cb_device_v1) ALWAYS sends
 *     `device_key_proof` (device-key.ts signRefresh). When the key cannot sign
 *     right now the refresh is not sent at all ('transient'): the session is
 *     kept and nothing is wiped. A non-terminal DEVICE_KEY_PROOF_INVALID /
 *     DEVICE_KEY_PROOF_REQUIRED answer is also 'transient' (usually a device
 *     clock more than 12 h off): the credential is not consumed server-side.
 *   - A terminal answer goes through handleTerminalAuth; any other definitive
 *     refusal drops the credential ('failed'), and the caller's 401 handling
 *     signs out as before.
 *
 * The shared API client (api.ts) calls refreshDeviceSession() before sending a
 * request with an expired bound token and once on a 401. Sessions registered
 * before this build have no stored credential: nothing changes for them.
 */
import { Capacitor } from '@capacitor/core';
import { useAuthStore } from '@/store/auth.store';
import { getNativeDeviceKeySigner, signRefresh, type DeviceKeySigner } from './device-key';
import type { TokenVault } from './native-token-vault';
import { DEVICE_REFRESH_VAULT_KEYS, VAULT_KEYS } from './vault-keys';
import { errorCodeOf, rawPost, type RawPost } from './raw-http';
import { handleTerminalAuth, terminalAuthCode } from './terminal-auth';

export const DEVICE_REFRESH_PATH = '/auth/device/refresh';

export type DeviceRefreshOutcome = 'refreshed' | 'failed' | 'transient';

/** Non-terminal refusals of the proof (backend verifyDeviceRefreshProof): keep everything, retry later. */
const PROOF_REFUSALS = new Set(['DEVICE_KEY_PROOF_INVALID', 'DEVICE_KEY_PROOF_REQUIRED']);

export interface DeviceRefreshDeps {
  post: RawPost;
  vault: () => Promise<TokenVault | null>;
  signer: () => DeviceKeySigner | null;
  isNative: () => boolean;
  now: () => number;
  randomBytes: (n: number) => Uint8Array;
}

const defaultDeps: DeviceRefreshDeps = {
  post: rawPost,
  // Loaded on first use: api.ts imports this module, and the vault module loads Capacitor plugins.
  vault: async () => (await import('./auth-token-persistence')).getSessionTokenVault(),
  signer: getNativeDeviceKeySigner,
  isNative: () => {
    try {
      return Capacitor.isNativePlatform();
    } catch {
      return false;
    }
  },
  now: () => Date.now(),
  randomBytes: (n) => crypto.getRandomValues(new Uint8Array(n)),
};

let deps: DeviceRefreshDeps = defaultDeps;
let inFlight: Promise<DeviceRefreshOutcome> | null = null;
/** false once the vault was read and held no credential; reset by storeDeviceRefreshCredential(). */
let credentialMayExist = true;

/** Test seam. */
export function __setDeviceRefreshDepsForTests(next: Partial<DeviceRefreshDeps> | null): void {
  deps = next ? { ...defaultDeps, ...next } : defaultDeps;
  inFlight = null;
  credentialMayExist = true;
}

function base64Url(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/**
 * The token's claims. Local rather than the store's decodeJwtPayload: this runs
 * inside every API request (api.ts), and must not depend on more of the store
 * than getState().
 */
function claimsOf(token: string | null): { sid?: unknown; exp?: unknown } | null {
  if (!token) return null;
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const decoded = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
    return decoded && typeof decoded === 'object' ? decoded : null;
  } catch {
    return null;
  }
}

function boundSessionId(token: string | null): string | null {
  const sid = claimsOf(token)?.sid;
  return typeof sid === 'string' && sid.length > 0 ? sid : null;
}

/** Expired, or within 60 s of it (the store's isTokenExpired rule). */
function expiresSoon(token: string): boolean {
  const exp = claimsOf(token)?.exp;
  return typeof exp !== 'number' || exp * 1000 <= Date.now() + 60_000;
}

/** Whether a 401 / an expired token on this client may be cured by a device refresh. */
export function canRefreshDeviceSession(): boolean {
  return credentialMayExist && deps.isNative() && boundSessionId(useAuthStore.getState().token) !== null;
}

/** Whether the bound token should be renewed before a request goes out with it. */
export function deviceTokenNeedsRefresh(): boolean {
  if (!canRefreshDeviceSession()) return false;
  const token = useAuthStore.getState().token;
  return !!token && expiresSoon(token);
}

/**
 * After a registration: keep the session's refresh credential and, for a
 * key-bound session, the bound key id. Any nonce of an older session is
 * dropped. Throws if the vault refuses (the caller keeps going: the bound
 * token still works until it expires).
 */
export async function storeDeviceRefreshCredential(
  vault: TokenVault,
  credential: { refreshToken: string; deviceKeyId: string | null }
): Promise<void> {
  await vault.remove(VAULT_KEYS.deviceRefreshNonce);
  await vault.set(VAULT_KEYS.deviceRefreshToken, credential.refreshToken);
  if (credential.deviceKeyId) await vault.set(VAULT_KEYS.deviceKeyId, credential.deviceKeyId);
  else await vault.remove(VAULT_KEYS.deviceKeyId);
  credentialMayExist = true;
}

/**
 * Forget the refresh credential after a refusal. Best effort. (Sign-out clears
 * the same keys in auth-token-persistence.ts, with the session token.)
 */
export async function clearDeviceRefreshCredential(vault?: TokenVault | null): Promise<void> {
  const v = vault ?? (await deps.vault().catch(() => null));
  if (!v) return;
  for (const key of DEVICE_REFRESH_VAULT_KEYS) {
    await v.remove(key).catch(() => undefined);
  }
}

async function doRefresh(): Promise<DeviceRefreshOutcome> {
  const token = useAuthStore.getState().token;
  const sid = boundSessionId(token);
  if (!sid) return 'failed';
  const vault = await deps.vault().catch(() => null);
  if (!vault) return 'transient';

  let refreshToken: string | null;
  let nonce: string | null;
  let boundKeyId: string | null;
  try {
    refreshToken = await vault.get(VAULT_KEYS.deviceRefreshToken);
    nonce = await vault.get(VAULT_KEYS.deviceRefreshNonce);
    boundKeyId = await vault.get(VAULT_KEYS.deviceKeyId);
  } catch {
    return 'transient'; // Keystore busy: never a reason to end the session.
  }
  if (!refreshToken) {
    credentialMayExist = false;
    return 'failed';
  }

  if (!nonce) {
    nonce = base64Url(deps.randomBytes(24));
    try {
      await vault.set(VAULT_KEYS.deviceRefreshNonce, nonce);
    } catch {
      return 'transient';
    }
  }

  const body: Record<string, unknown> = { refresh_token: refreshToken, nonce };
  if (boundKeyId) {
    // Signed after the nonce is fixed: the nonce is part of the signed text.
    const signed = await signRefresh(deps.signer(), { deviceSessionId: sid, refreshToken, nonce, now: deps.now() });
    if (!signed) return 'transient';
    body.device_key_proof = signed.proof;
  }

  let res;
  try {
    res = await deps.post(DEVICE_REFRESH_PATH, body, {}, { timeoutMs: 15_000 });
  } catch {
    return 'transient';
  }

  if (res.status === 200) {
    const data = res.data as { token?: unknown; refresh_token?: unknown } | null;
    if (typeof data?.token !== 'string' || typeof data.refresh_token !== 'string') return 'transient';
    // Signed out, or another sign-in, while the request was in flight: never
    // put this session's token back, and never sign out the new one.
    if (useAuthStore.getState().token !== token) return 'transient';
    try {
      await vault.set(VAULT_KEYS.deviceRefreshToken, data.refresh_token);
      await vault.remove(VAULT_KEYS.deviceRefreshNonce);
    } catch {
      // Not stored: use the new access token now, but keep presenting the OLD
      // credential with the SAME nonce (the server answers it with this same
      // successor) until the successor can be stored.
    }
    useAuthStore.getState().setToken(data.token);
    return 'refreshed';
  }

  const code = errorCodeOf(res.data);
  if (res.status === 429 || res.status === 409 || res.status >= 500) return 'transient';
  const terminal = terminalAuthCode(res.status, res.data);
  if (!terminal && code && PROOF_REFUSALS.has(code)) return 'transient';

  await clearDeviceRefreshCredential(vault);
  credentialMayExist = false;
  if (terminal) void handleTerminalAuth(terminal);
  return 'failed';
}

/** Renew the bound token. Single-flight; never throws. */
export function refreshDeviceSession(): Promise<DeviceRefreshOutcome> {
  if (!inFlight) {
    inFlight = doRefresh()
      .catch((): DeviceRefreshOutcome => 'transient')
      .finally(() => {
        inFlight = null;
      });
  }
  return inFlight;
}
